import { useState, useRef } from 'react';
import { distanceImperialToMetric } from 'utils/unitConversions';
import { enqueueSnackbar } from 'notistack';
import {
  MineModelDto,
  ProjectDto,
  useDufImportMutation,
  FileParameter,
  ImportDufFileMutation,
  FileResponse,
  ImportDufResponse,
  DufEntityDto,
  useImportDufFile,
  PipeDataDto
} from 'providers/api';
import { PropertyMappings } from '../components/PropertyMappingDialog/PropertyMappingDialog';
import {
  buildImportPayload,
  checkDistanceToCenterForPCNodes,
  dedupeEdges,
  idMapFromRepoints,
  ImportGraph,
  previewNodesToModel,
  remapEdges,
  resolveIntersectionsAndSplitLines
} from './sharedImportUtils';
import { buildNodeSpatialHash } from '../utils/spatialHash';
import { Model } from '../../hooks/useModelState';
import { PCNode } from '../../MineModel/Nodes';
import Vector from '../../MineModel/Nodes/Vector';
import { validateNodes } from '../utils/validation';
import { SpoolEdge } from '../utils/nodeCreation';
import PCSpool from '../../MineModel/Spools/PCSpool';

interface UseDufImportProps {
  project: ProjectDto;
  mineModel: MineModelDto;
  model: Model;
  isAdminOrPc?: boolean;
}

interface LayerTreeNode {
  name: string;
  guid?: string;
  layer?: DufEntityDto;
  children?: LayerTreeNode[];
}

const isPolylineEntity = (entity: DufEntityDto) => entity.type !== 'Point' && (entity.vertexList?.length ?? 0) > 1;

const useDufImport = ({ model, project, mineModel, isAdminOrPc }: UseDufImportProps) => {
  const [isDufImporting, setIsImporting] = useState(false);
  const [dufError, setError] = useState<string | null>(null);
  const [isDufPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewNodesList, setPreviewNodes] = useState<PCNode[]>([]);
  const [previewEdges, setPreviewEdges] = useState<SpoolEdge[]>([]);
  const [previewSpools, setPreviewSpools] = useState<PCSpool[]>([]);
  const importMutation = useImportDufFile(project.entityId);
  const mutation = useDufImportMutation();

  // For errors found in the browser; a failed request is already raised by the global mutation error handler.
  const showError = (message: string) => {
    setError(message);
    enqueueSnackbar(message, { variant: 'error', autoHideDuration: 5000 });
  };

  const removePreviewNodes = () => {
    if (!model || previewNodesList.length === 0) return;
    model.removeNodes(previewNodesList);
    if (previewSpools.length > 0) {
      model.removeSpools(previewSpools);
    }
    setPreviewNodes([]);
    setPreviewEdges([]);
    setPreviewSpools([]);
  };
  const [layerDialogOpen, setLayerDialogOpen] = useState(false);
  const [availableLayers, setAvailableLayers] = useState<LayerTreeNode[]>([]);
  const [selectedLayerGuids, setSelectedLayerGuids] = useState<string[]>([]);
  const availableLayersRawRef = useRef<Record<string, DufEntityDto[]> | null>(null);
  const [modelEntities, setModelEntities] = useState<any[]>([]);
  const [isImperial, setIsImperial] = useState<boolean>(false);
  const [propertyMappingDialogOpen, setPropertyMappingDialogOpen] = useState(false);
  const [availableProperties, setAvailableProperties] = useState<string[]>([]);
  const [availableVertexAttributes, setAvailableVertexAttributes] = useState<string[]>([]);
  const [uniquePropertyValues, setUniquePropertyValues] = useState<Record<string, string[]>>({});
  const [selectedEntities, setSelectedEntities] = useState<DufPolylineEntity[]>([]);

  interface DufImportVertex {
    x: number;
    y: number;
    z: number;
    attributes?: Record<string, any>;
  }

  interface DufPolylineEntity {
    guid: string;
    type: 'PolyLine' | 'Line' | 'Other';
    name?: string;
    vertexList: DufImportVertex[];
    xProperties?: Record<string, any>;
    vertexAttributeNames?: string[];
  }

  const buildLayerTree = (layers: DufEntityDto[]): LayerTreeNode[] => {
    const rootMap: Record<string, any> = {};

    layers.forEach((layer) => {
      const path = (layer.name || layer.guid).replace(/\\/g, '/').split('/');
      let currentLevel = rootMap;

      for (let i = 0; i < path.length; i += 1) {
        const part = path[i];
        if (!currentLevel[part]) {
          currentLevel[part] = {
            name: part,
            children: {},
          };
        }

        if (i === path.length - 1) {
          currentLevel[part].guid = layer.guid;
          currentLevel[part].layer = layer;
        }

        currentLevel = currentLevel[part].children;
      }
    });

    const convertToArray = (nodeMap: Record<string, any>): LayerTreeNode[] => Object.values(nodeMap).map((node) => ({
      name: node.name,
      guid: node.guid,
      layer: node.layer,
      children: convertToArray(node.children),
    }));
    return convertToArray(rootMap);
  };

  const extractPropertyValue = (propertyValue: any): string => {
    if (propertyValue !== null && typeof propertyValue === 'object' && propertyValue.value && Array.isArray(propertyValue.value) && propertyValue.value.length > 0) {
      return String(propertyValue.value[0].value || propertyValue.value[0].valueString || '');
    }
    return String(propertyValue || '');
  };

  const generateUniqueId = (baseId: string, existingIds: Set<string>, usedIds: Set<string>): string => {
    let finalId = baseId;
    let counter = 1;

    while (existingIds.has(finalId) || usedIds.has(finalId)) {
      finalId = `${baseId}_${String(counter).padStart(2, '0')}`;
      counter += 1;
    }

    usedIds.add(finalId);
    return finalId;
  };

  const extractVertexAttributeNames = (entities: DufPolylineEntity[]): string[] => {
    const names = new Set<string>();

    entities.forEach(({ vertexAttributeNames, vertexList }) => {
      (vertexAttributeNames || []).forEach((name) => names.add(name));
      (vertexList || []).forEach(({ attributes }) => Object.keys(attributes || {}).forEach((name) => names.add(name)));
    });

    return Array.from(names).sort();
  };

  const processPolylines = (DufLines: DufPolylineEntity[], pipeId: string, mappings?: PropertyMappings): ImportGraph => {
    const nodeIdAttribute = mappings?.nodeIdAttribute || undefined;
    const pipeTypeProperty = mappings?.pipeTypeProperty?.split(' - ')[0] || undefined;
    const nodeMap: Map<string, PCNode> = new Map();
    const existingNodesArray = Object.values(model.nodes || {});
    const existingNodeMap = new Map(
      existingNodesArray.map((node) => [
        `${node.coords.x.toFixed(2)},${node.coords.y.toFixed(2)},${node.coords.z.toFixed(2)}`,
        node,
      ]),
    );
    const existingNodeIds = new Set(existingNodesArray.map((node) => node.id));
    const existingHash = buildNodeSpatialHash(existingNodesArray, 1.0);
    const usedIds = new Set<string>();
    const importedNodes = new Set<PCNode>();
    let deswikCounter = 1;

    const edges: SpoolEdge[] = [];

    // The line, not either endpoint, decides the pipe - a DUF layer can map its own pipe type - so the
    // pipe goes onto the edge. An edge between two nodes that were both already in the model is left
    // alone: the import is adding pipes, not re-authoring the network it connects to.
    const connectImportedNode = (source: PCNode | undefined, target: PCNode | undefined, pipeTypeId: string) => {
      if (!source || !target || source.id === target.id) return;
      if (!importedNodes.has(source) && !importedNodes.has(target)) return;
      edges.push({ nodeAId: source.id, nodeBId: target.id, pipeId: pipeTypeId });
    };

    DufLines.forEach((line) => {
      if (!line.vertexList || line.vertexList.length < 2) return;
      let previousNode: PCNode | undefined;

      let linePipeId = pipeId;
      if (pipeTypeProperty && line.xProperties && line.xProperties[pipeTypeProperty]) {
        const propertyValue = extractPropertyValue(line.xProperties[pipeTypeProperty]);

        if (propertyValue) {
          linePipeId = mappings?.pipeTypeMappings?.[propertyValue] || pipeId;
        }
      }

      line.vertexList.forEach((vertex) => {
        let vertexX = vertex.x;
        let vertexY = vertex.y;
        let vertexZ = vertex.z;

        if (isImperial) {
          vertexX = distanceImperialToMetric(vertexX);
          vertexY = distanceImperialToMetric(vertexY);
          vertexZ = distanceImperialToMetric(vertexZ);
        }

        const key = `${vertexX.toFixed(2)},${vertexY.toFixed(2)},${vertexZ.toFixed(2)}`;
        let node = existingNodeMap.get(key) || nodeMap.get(key);

        if (!node) {
          const nearbyNode = existingHash.findNearest(vertexX, vertexY, vertexZ, 1.0);
          if (nearbyNode) {
            node = nearbyNode;
          }
        }

        if (!node) {
          const vector = new Vector(vertexX, vertexY, vertexZ);
          let baseNodeId = nodeIdAttribute ? extractPropertyValue(vertex.attributes?.[nodeIdAttribute]).trim() : '';
          if (!baseNodeId) {
            let candidateId = `Deswik_${String(deswikCounter).padStart(4, '0')}`;
            while (existingNodeIds.has(candidateId) || usedIds.has(candidateId)) {
              deswikCounter += 1;
              candidateId = `Deswik_${String(deswikCounter).padStart(4, '0')}`;
            }
            baseNodeId = candidateId;
            deswikCounter += 1;
          }

          const nodeId = generateUniqueId(baseNodeId, existingNodeIds, usedIds);

          node = new PCNode({ coords: vector, id: nodeId, pipeId: linePipeId });
          nodeMap.set(key, node);
          importedNodes.add(node);
        }

        connectImportedNode(previousNode, node, linePipeId);

        previousNode = node;
      });
    });

    return { nodes: Array.from(nodeMap.values()), edges: dedupeEdges(edges) };
  };

  // Validation renames nodes whose id is taken and drops nodes that land on an existing node's
  // coordinates. Both cases hand the node's edges to a different id, so `spoolsToRepoint` and the rename
  // map are applied to the edge list too. An edge left pointing at a dropped id is pruned by the server
  // on load and the imported pipe disappears without an error being surfaced.
  const processDufEntities = (dufEntities: DufPolylineEntity[], pipeId: string, mappings?: PropertyMappings): ImportGraph => {
    const lines = dufEntities.filter((entity) => entity.vertexList && entity.vertexList.length > 1);
    const graph = resolveIntersectionsAndSplitLines(processPolylines(lines, pipeId, mappings), 0.5);

    if (graph.nodes.length === 0 || !model) return graph;

    const existingNodes = Object.values(model.nodes);
    const validationResult = validateNodes(graph.nodes, existingNodes, 1.0);
    if (validationResult.isValid) return graph;

    if (validationResult.duplicateCoords && validationResult.duplicateCoords.length > 0) {
      const idMap = idMapFromRepoints(validationResult);
      const nodes = graph.nodes.filter((node) => !validationResult.nodesToRemove?.includes(node.id));

      return { nodes, edges: remapEdges(graph.edges, idMap) };
    }

    if (validationResult.duplicateIds && validationResult.duplicateIds.length > 0) {
      const existingNodeIds = new Set(existingNodes.map((node) => node.id));
      const usedIds = new Set(graph.nodes.map((node) => node.id));
      const idMap = new Map<string, string>();

      validationResult.duplicateIds.forEach((duplicateId) => {
        idMap.set(duplicateId, generateUniqueId(duplicateId, existingNodeIds, usedIds));
      });

      graph.nodes.forEach((node) => {
        const renamed = idMap.get(node.id);
        if (renamed) Object.assign(node, { id: renamed });
      });

      return { nodes: graph.nodes, edges: remapEdges(graph.edges, idMap) };
    }

    return graph;
  };

  const extractPropertiesFromEntities = (entities: DufPolylineEntity[]) => {
    const allProperties = new Set<string>();
    const propertyValues: Record<string, Set<string>> = {};
    const propertyToLayers: Record<string, Set<string>> = {};

    entities.forEach((entity: any) => {
      const layerName = entity.name || 'Unknown Layer';

      if (entity.xProperties) {
        Object.keys(entity.xProperties).forEach((propertyName) => {
          const propertyWithLayer = `${propertyName} - ${layerName}`;
          allProperties.add(propertyWithLayer);

          if (!propertyValues[propertyWithLayer]) {
            propertyValues[propertyWithLayer] = new Set();
          }

          if (!propertyToLayers[propertyName]) {
            propertyToLayers[propertyName] = new Set();
          }
          propertyToLayers[propertyName].add(layerName);

          const value = entity.xProperties![propertyName];
          if (value !== null && value !== undefined) {
            let stringValue: string;
            if (typeof value === 'object' && value.value && Array.isArray(value.value) && value.value.length > 0) {
              const actualValue = value.value[0].value || value.value[0].valueString;
              stringValue = String(actualValue);
            } else if (typeof value === 'object') {
              stringValue = JSON.stringify(value);
            } else {
              stringValue = String(value);
            }
            propertyValues[propertyWithLayer].add(stringValue);
          }
        });
      }
    });

    const uniqueValues: Record<string, string[]> = {};
    Object.keys(propertyValues).forEach((prop) => {
      uniqueValues[prop] = Array.from(propertyValues[prop]).sort();
    });

    return {
      properties: Array.from(allProperties).sort(),
      uniqueValues,
      propertyToLayers,
    };
  };

  const handleLayerDialogCancel = () => {
    setLayerDialogOpen(false);
    setAvailableLayers([]);
    setSelectedLayerGuids([]);
    setPropertyMappingDialogOpen(false);
    setSelectedEntities([]);
  };

  const previewDufNodes = (graph: ImportGraph, pipeData: PipeDataDto) => {
    if (!model) {
      showError('Model state not initialized');
      return false;
    }
    if (graph.nodes.length === 0) {
      showError('No importable DUF reticulation pipes were found in the selected layers.');
      return false;
    }
    let addedSpools: PCSpool[] = [];
    try {
      addedSpools = previewNodesToModel(graph, model, pipeData);

      if (addedSpools.length === 0) {
        showError('No pipes were created from the selected DUF layers. Select line or polyline entities with at least two connected vertices.');
        model.removeNodes(graph.nodes);
        setPreviewNodes([]);
        setPreviewEdges([]);
        setPreviewSpools([]);
        return false;
      }

      setPreviewNodes(graph.nodes);
      setPreviewEdges(graph.edges);
      setPreviewSpools(addedSpools);
      return true;
    } catch {
      showError('Error previewing nodes');
      if (graph.nodes.length > 0) {
        model.removeNodes(graph.nodes);
      }
      if (addedSpools.length > 0) {
        model.removeSpools(addedSpools);
      }
      return false;
    }
  };

  const handleSaveDufNodes = async (graphToSave?: ImportGraph) => {
    const nodes = graphToSave?.nodes ?? previewNodesList;
    const edges = graphToSave?.edges ?? previewEdges;
    if (!nodes || nodes.length === 0) {
      showError('No nodes to save');
      return;
    }
    try {
      setIsImporting(true);
      setError(null);
      const payload = buildImportPayload(nodes, edges);

      mutation.mutate(
        {
          projectId: project.entityId,
          data: payload,
          projectName: project.name,
          nodeId: '',
        },
        {
          onSuccess: async () => {
            setError(null);
            setIsPreviewOpen(false);
            setPreviewNodes([]);
            setPreviewEdges([]);
            setPreviewSpools([]);
          },
          onError: () => {
            setError('Failed to save nodes. Please try again.');
            removePreviewNodes();
          },
        },
      );
    } catch {
      showError('Failed to save nodes');
      removePreviewNodes();
    } finally {
      setIsImporting(false);
    }
  };

  const handleLayerDialogConfirm = () => {
    setLayerDialogOpen(false);
    const allEntitiesRaw = availableLayersRawRef.current;
    if (!allEntitiesRaw) return;

    const entities = ((allEntitiesRaw.ModelEntities ?? []) as DufEntityDto[]).filter(isPolylineEntity);
    const layers = (allEntitiesRaw.Layers ?? []) as DufEntityDto[];
    const guidsWithoutPolylines: string[] = [];
    const polylineList: DufEntityDto[] = [];

    selectedLayerGuids.forEach((layerGuid) => {
      const layer = layers.find((l) => l.guid === layerGuid);
      const layerName = layer?.name;

      const layerEntities = entities.filter((e: any) => {
        const entityLayerKey = e.name;
        return (
          (entityLayerKey && entityLayerKey === layerGuid) || (entityLayerKey && layerName && entityLayerKey === layerName));
      });

      if (layerEntities.length === 0) {
        guidsWithoutPolylines.push(layerGuid);
        return;
      }
      polylineList.push(...layerEntities);
    });

    if (guidsWithoutPolylines.length > 0) {
      const message = `No entities found in the selected layers with GUIDs:\n${guidsWithoutPolylines
        .map((g, i) => `${i === 0 ? '' : ','} GUID ${g}`)
        .join('\n')}`;
      showError(message);
      return;
    }

    const selectedItems: DufPolylineEntity[] = polylineList.map((e) => ({
      guid: e.guid,
      type: (e.type as DufPolylineEntity['type']) || 'Other',
      name: e.name ?? undefined,
      vertexList: (e.vertexList ?? []).map(({ x, y, z, attributes }) => ({
        x,
        y,
        z,
        attributes: attributes ?? undefined,
      })),
      xProperties: e.xProperties ?? undefined,
      vertexAttributeNames: e.vertexAttributeNames ?? undefined,
    }));
    setSelectedEntities(selectedItems);

    const { properties, uniqueValues } = extractPropertiesFromEntities(selectedItems);
    setAvailableProperties(properties);
    setUniquePropertyValues(uniqueValues);
    setAvailableVertexAttributes(extractVertexAttributeNames(selectedItems));

    setPropertyMappingDialogOpen(true);
  };

  const handlePropertyMappingConfirm = (mappings: PropertyMappings) => {
    setPropertyMappingDialogOpen(false);

    const pipeDataList = mineModel.reticulationData?.pipeData;
    if (!pipeDataList || pipeDataList.length === 0) {
      showError('No pipe data available. Please select pipes first.');
      return;
    }
    const pipeData = pipeDataList[0];
    const graph = processDufEntities(selectedEntities, pipeData?.pipeId ?? '', mappings);

    if (graph.nodes.length === 0) {
      showError('No importable DUF reticulation pipes were found in the selected layers.');
      return;
    }

    if (!isAdminOrPc) {
      const distanceResult = checkDistanceToCenterForPCNodes(model, graph.nodes);
      if (!distanceResult.isValid) {
        showError(distanceResult.errors.join(', '));
        return;
      }
    }

    if (!previewDufNodes(graph, pipeData as PipeDataDto)) {
      return;
    }

    setError(null);
    setIsPreviewOpen(true);

    setAvailableLayers([]);
    setSelectedLayerGuids([]);
  };

  const handlePropertyMappingCancel = () => {
    setPropertyMappingDialogOpen(false);
    setSelectedEntities([]);
    setAvailableProperties([]);
    setAvailableVertexAttributes([]);
    setUniquePropertyValues({});
  };

  const handleImportDuf = async (file: File) => {
    const pipeDataList = mineModel.reticulationData?.pipeData;
    if (!pipeDataList || pipeDataList.length === 0) {
      showError('No pipe data available. Please select pipes first.');
      return;
    }
    const fileParam: FileParameter = {
      data: file,
      fileName: file.name,
    };

    const payload: ImportDufFileMutation = { fileParam };

    setIsImporting(true);
    setError(null);

    importMutation.mutate(payload, {
      onSuccess: async (response: FileResponse) => {
        try {
          const text = await response.data.text();
          const allEntities: ImportDufResponse = JSON.parse(text);
          const allEntitiesRaw = allEntities.entities ?? {};
          availableLayersRawRef.current = allEntitiesRaw;
          const allLayers = allEntitiesRaw.Layers ?? [];
          const entities = (allEntitiesRaw.ModelEntities ?? []).filter(isPolylineEntity);
          const { imperialUnits } = allEntities;
          const relevantLayerKeys = new Set(
            entities.map((e) => e.name as string | undefined).filter((x): x is string => !!x),
          );

          const filteredLayers = allLayers.filter(
            (layer: DufEntityDto) => (layer.guid && relevantLayerKeys.has(layer.guid)) || (layer.name && relevantLayerKeys.has(layer.name)),
          );

          if (filteredLayers.length === 0) {
            showError('No line or polyline reticulation layers were found in the DUF file.');
            setIsImporting(false);
            return;
          }

          setModelEntities(entities);
          const tree = buildLayerTree(filteredLayers);
          setAvailableLayers(tree);
          setSelectedLayerGuids([]);
          setLayerDialogOpen(true);
          setIsImperial(imperialUnits);
          setTimeout(() => setIsImporting(false), 100);
        } catch (err) {
          showError('Failed to process DUF file response.');
          setIsImporting(false);
        }
      },
      onError: (err: any) => {
        // Extract error message from API response
        const errorMessage = err?.result?.errors?.['']?.[0]
          || err?.result?.title
          || err?.response?.title
          || err?.message
          || 'Failed to import DUF file';
        setError(errorMessage);
        setIsImporting(false);
      },
    });
  };

  const handleRemoveDufPreview = () => {
    if (!model) {
      showError('Model state not initialized');
      return;
    }
    removePreviewNodes();
    setIsPreviewOpen(false);
  };

  return {
    isDufImporting,
    dufError,
    isDufPreviewOpen,
    previewNodesList,
    selectedLayerGuids,
    layerDialogOpen,
    availableLayers,
    modelEntities,
    propertyMappingDialogOpen,
    availableProperties,
    availableVertexAttributes,
    uniquePropertyValues,
    previewDufNodes,
    setSelectedLayerGuids,
    handleImportDuf,
    handleSaveDufNodes,
    handleRemoveDufPreview,
    handleLayerDialogCancel,
    handleLayerDialogConfirm,
    handlePropertyMappingConfirm,
    handlePropertyMappingCancel,
  };
};

export default useDufImport;
